import { Link, useNavigate } from 'react-router-dom';
import { User, Menu, X } from 'lucide-react';
import { useState } from 'react';
import { Button } from './ui/Button';
import { useAuth } from './AuthProvider';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useTranslation } from 'react-i18next';
import LanguageSelector from './LanguageSelector';
import { capture } from '@/lib/analytics/posthog';

export default function HomeHeader({ hideAuthLinks = false, transparent = false }: { hideAuthLinks?: boolean; transparent?: boolean }) {
    const { user, userRoles, signOut } = useAuth();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

    const closeMobileMenu = () => setIsMobileMenuOpen(false);

    const handleSignOut = async () => {
        try {
            await signOut();
        } catch (error) {
            console.error('Error signing out:', error);
        }
    };

    const handleDashboardClick = () => {
        if (userRoles?.creator) {
            navigate('/creator');
        } else {
            navigate('/dashboard');
        }
    };

    return (
        <header className={transparent ? "absolute top-0 left-0 right-0 z-50 bg-transparent" : "bg-white"}>
            <nav className="max-w-7xl mx-auto px-6 py-6 flex items-center justify-between" aria-label="Global">
                <div className="flex items-center">
                    <Link to="/" className="-m-1.5 p-1.5 flex items-center">
                        <img
                            className="h-12 md:h-16 w-auto object-contain"
                            src="/mystudyfund-logo.png"
                            alt="My Study Fund"
                        />
                    </Link>
                </div>

                {/* Desktop nav */}
                <div className="hidden md:flex items-center gap-8">
                    <Link
                        to="/about"
                        className="text-primary/80 hover:text-primary transition-colors font-medium"
                    >
                        {t('nav.about')}
                    </Link>
                    <Link
                        to="/how-it-works"
                        className="text-primary/80 hover:text-primary transition-colors font-medium"
                    >
                        {t('nav.howItWorks')}
                    </Link>
                    <Link
                        to="/faqs"
                        className="text-primary/80 hover:text-primary transition-colors font-medium"
                    >
                        {t('nav.pricing')}
                    </Link>
                    <Link
                        to="/blog"
                        className="text-primary/80 hover:text-primary transition-colors font-medium"
                    >
                        {t('nav.blog')}
                    </Link>
                </div>

                {/* Desktop auth/actions */}
                <div className="hidden md:flex items-center gap-4">
                    <LanguageSelector />
                    {user ? (
                        <DropdownMenu>
                            <DropdownMenuTrigger className="flex items-center justify-center w-10 h-10 rounded-full bg-gray-100 hover:bg-gray-200">
                                <User className="w-5 h-5 text-gray-600" />
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-48">
                                <DropdownMenuItem onClick={handleDashboardClick}>
                                    {t('nav.dashboard')}
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={handleSignOut}>
                                    {t('actions.logout')}
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    ) : hideAuthLinks ? null : (
                        <>
                            <Link to="/signin">
                                <Button
                                    variant="ghost"
                                    className="text-primary hover:bg-primary/10"
                                >
                                    {t('actions.login')}
                                </Button>
                            </Link>
                            <Link
                                to="/signup-creator"
                                onClick={() => capture('cta_clicked', { cta: 'start_fund_page', location: 'nav_header' })}
                            >
                                <Button
                                    className="bg-primary hover:bg-primary/90 text-primary-foreground rounded-full font-semibold"
                                >
                                    {t('actions.getStarted')}
                                </Button>
                            </Link>
                        </>
                    )}
                </div>

                {/* Mobile hamburger */}
                <button
                    type="button"
                    className="md:hidden inline-flex items-center justify-center rounded-md p-2 text-primary"
                    onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                    aria-label="Toggle menu"
                >
                    {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
                </button>
            </nav>

            {/* Mobile menu */}
            {isMobileMenuOpen && (
                <div className="md:hidden border-t border-gray-200 bg-white">
                    <div className="px-6 py-4 space-y-3">
                        <Link
                            to="/about"
                            onClick={closeMobileMenu}
                            className="block w-full text-left text-primary font-medium py-2"
                        >
                            {t('nav.about')}
                        </Link>
                        <Link
                            to="/how-it-works"
                            onClick={closeMobileMenu}
                            className="block w-full text-left text-primary font-medium py-2"
                        >
                            {t('nav.howItWorks')}
                        </Link>
                        <Link
                            to="/faqs"
                            onClick={closeMobileMenu}
                            className="block w-full text-left text-primary font-medium py-2"
                        >
                            {t('nav.pricing')}
                        </Link>
                        <Link
                            to="/blog"
                            onClick={closeMobileMenu}
                            className="block w-full text-left text-primary font-medium py-2"
                        >
                            {t('nav.blog')}
                        </Link>
                        <div className="pt-2 border-t border-gray-100">
                            <LanguageSelector />
                        </div>
                        {user ? (
                            <div className="pt-2 space-y-2">
                                <Button
                                    variant="ghost"
                                    className="w-full justify-start text-primary"
                                    onClick={() => { closeMobileMenu(); handleDashboardClick(); }}
                                >
                                    {t('nav.dashboard')}
                                </Button>
                                <Button
                                    variant="ghost"
                                    className="w-full justify-start text-primary"
                                    onClick={() => { closeMobileMenu(); handleSignOut(); }}
                                >
                                    {t('actions.logout')}
                                </Button>
                            </div>
                        ) : hideAuthLinks ? null : (
                            <div className="pt-2 space-y-2">
                                <Link to="/signin" onClick={closeMobileMenu} className="block">
                                    <Button
                                        variant="ghost"
                                        className="w-full text-primary"
                                    >
                                        {t('actions.login')}
                                    </Button>
                                </Link>
                                <Link
                                    to="/signup-creator"
                                    onClick={() => {
                                        capture('cta_clicked', { cta: 'start_fund_page', location: 'nav_header_mobile' });
                                        closeMobileMenu();
                                    }}
                                    className="block"
                                >
                                    <Button
                                        className="w-full bg-primary hover:bg-primary/90 text-primary-foreground rounded-full font-semibold"
                                    >
                                        {t('actions.getStarted')}
                                    </Button>
                                </Link>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </header>
    );
} 